import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  Client,
  IFrame,
  IMessage,
  StompSubscription,
} from '@stomp/stompjs';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  IFullWhatsappMessage,
  IWhatsappChatActivityInvalidation,
  IWhatsappChatProjectionInvalidation,
} from 'projects/mind-your-tax/myt/modules/whatsapp/modules/messenger/model/whatsapp.model';
import { environment } from 'projects/mind-your-tax/src/environments/environment';

/**
 * After this many consecutive failed WS handshakes we stop trying to
 * reconnect. Prevents the "infinite loop" symptom on localhost when no
 * STOMP backend is running — without a cap, StompJS would retry every
 * `reconnectDelay` ms (5s) forever, flooding the console and chewing
 * CPU. In production the broker should be reachable on first attempt;
 * if it isn't, 5 retries gives ~25s of "we tried" before giving up.
 * User can recover by calling `connect()` again (e.g. a Reconnect
 * button) or by refreshing the page.
 */
const MAX_RECONNECT_ATTEMPTS = 5;

export interface IIncomingCallAlert {
  sessionId: string;
  customerPhone: string;
  customerName: string | null;
  enquiryId: string | null;
  leadId: string | null;
  ringingAt: string;
  targeted: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private readonly isBrowser: boolean;
  private readonly allNotificationsSubject = new ReplaySubject<any>(1);
  private readonly ownNotificationsSubject = new ReplaySubject<any>(1);
  private readonly whatsappMessagesSubject =
    new ReplaySubject<IFullWhatsappMessage>(1);
  private readonly whatsappChatProjectionSubject =
    new Subject<IWhatsappChatProjectionInvalidation>();
  // W7.277j — content-free activity-created signal. Plain Subject (no
  // replay): a late subscriber must NOT act on a stale activity event;
  // authoritative timeline loads on select/reconnect are the recovery path.
  private readonly whatsappChatActivitySubject =
    new Subject<IWhatsappChatActivityInvalidation>();
  // A ring is an ephemeral event: late subscribers must never receive an
  // earlier call after it has stopped ringing.
  private readonly incomingCallAlertsSubject =
    new Subject<IIncomingCallAlert>();
  // Real STOMP connection status (driven by the client's onConnect /
  // onWebSocketClose callbacks). Consumers use this to reconcile state after a
  // reconnect. ReplaySubject(1) so late subscribers get the current status.
  private readonly connectionStatusSubject = new ReplaySubject<
    'connected' | 'disconnected'
  >(1);
  private client?: Client;
  private connectRequestCount = 0;
  private allSubscription?: StompSubscription;
  private ownSubscription?: StompSubscription;
  private whatsappSubscription?: StompSubscription;
  private whatsappChatProjectionSubscription?: StompSubscription;
  private whatsappChatActivitySubscription?: StompSubscription;
  private incomingCallUserSubscription?: StompSubscription;
  private incomingCallBroadcastSubscription?: StompSubscription;
  private consecutiveFailures = 0;
  private gaveUp = false;
  /**
   * Emits `true` the moment we exhaust `MAX_RECONNECT_ATTEMPTS` and
   * stop retrying, `false` after a successful reconnect or an
   * explicit `connect()` reset. Consumers (like the WhatsApp
   * messenger surface) subscribe to open a "connection lost, please
   * refresh" prompt.
   */
  private readonly gaveUpSubject = new BehaviorSubject<boolean>(false);
  readonly gaveUp$: Observable<boolean> = this.gaveUpSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  connect(): void {
    if (!this.isBrowser) return;

    // A previous run already exhausted the retry budget — calling
    // connect() again is the user's explicit "try again" signal so we
    // reset the failure counter and let the client re-activate.
    if (this.gaveUp) {
      console.log(
        '[WebSocket] connect() resetting after previous give-up; will retry',
      );
      this.gaveUp = false;
      this.consecutiveFailures = 0;
      // Consumers watching `gaveUp$` (e.g. the WhatsApp messenger's
      // refresh-prompt dialog) see the state flip back to healthy so
      // they can dismiss the prompt or reset any one-shot flags.
      this.gaveUpSubject.next(false);
      if (this.client) {
        this.client.reconnectDelay = 5000;
      }
    }

    this.connectRequestCount += 1;
    console.log(
      '[WebSocket] connect requested',
      'refCount=', this.connectRequestCount,
      'url=', this.websocketUrl(),
    );
    this.ensureClient();

    if (!this.client?.active) {
      console.log('[WebSocket] activating client');
      this.client?.activate();
    }
  }

  disconnect(): void {
    if (!this.isBrowser) return;

    if (this.connectRequestCount > 0) {
      this.connectRequestCount -= 1;
    }

    console.log(
      '[WebSocket] disconnect requested',
      'refCount=', this.connectRequestCount,
    );

    if (this.connectRequestCount > 0) {
      return;
    }

    this.unsubscribeAll();
    if (this.client?.active) {
      console.log('[WebSocket] deactivating client');
      void this.client.deactivate();
    }
  }

  getAllNotifications(): Observable<any> {
    return this.isBrowser ? this.allNotificationsSubject.asObservable() : EMPTY;
  }

  getOwnNotifications(): Observable<any> {
    return this.isBrowser ? this.ownNotificationsSubject.asObservable() : EMPTY;
  }

  getWhatsappMessages(): Observable<IFullWhatsappMessage> {
    return this.isBrowser ? this.whatsappMessagesSubject.asObservable() : EMPTY;
  }

  getWhatsappChatProjectionInvalidations(): Observable<IWhatsappChatProjectionInvalidation> {
    return this.isBrowser
      ? this.whatsappChatProjectionSubject.asObservable()
      : EMPTY;
  }

  /**
   * W7.277j — strict content-free activity-created events from
   * `/whatsapp/chat-activity`. Emits only payloads that pass
   * `parseChatActivityInvalidation` (exactly two positive safe-integer IDs);
   * anything else — extra keys, content-bearing fields, arrays, strings — is
   * dropped without logging the payload body.
   */
  getWhatsappChatActivityInvalidations(): Observable<IWhatsappChatActivityInvalidation> {
    return this.isBrowser
      ? this.whatsappChatActivitySubject.asObservable()
      : EMPTY;
  }

  getIncomingCallAlerts(): Observable<IIncomingCallAlert> {
    return this.isBrowser
      ? this.incomingCallAlertsSubject.asObservable()
      : EMPTY;
  }

  /**
   * Real connection status, emitted from the STOMP client's onConnect /
   * onWebSocketClose callbacks. Unlike a synchronous optimistic flag, this
   * reflects the actual socket, so subscribers can trigger a state reconcile on
   * every genuine (re)connect. See docs/unread-count-sync-frontend.md.
   */
  getConnectionStatus(): Observable<'connected' | 'disconnected'> {
    return this.isBrowser ? this.connectionStatusSubject.asObservable() : EMPTY;
  }

  private ensureClient(): void {
    if (this.client) {
      return;
    }

    this.client = new Client({
      webSocketFactory: () => new WebSocket(this.websocketUrl()),
      reconnectDelay: 5000,
      heartbeatIncoming: 30000,
      heartbeatOutgoing: 30000,
      connectionTimeout: 10000,
      discardWebsocketOnCommFailure: true,
      connectHeaders: {},
      onConnect: (frame: IFrame): void => {
        console.log('[WebSocket] connected', frame.headers);
        // A successful handshake resets the consecutive-failure budget
        // so a flaky network that recovers after a few retries doesn't
        // accidentally trip the give-up threshold next time it blips.
        this.consecutiveFailures = 0;
        // Also flip `gaveUp$` back to healthy — a retry that succeeds
        // before hitting the cap after a previous give-up (e.g. the
        // operator clicked "retry" and it worked) dismisses any
        // consumer-side "refresh please" state.
        if (this.gaveUpSubject.value) {
          this.gaveUpSubject.next(false);
        }
        this.subscribeToChannels();
        this.connectionStatusSubject.next('connected');
      },
      onStompError: (frame: IFrame): void => {
        console.error(
          '[WebSocket] broker error',
          frame.headers,
          frame.body
        );
      },
      onWebSocketError: (event: Event): void => {
        console.error('[WebSocket] socket error', event);
      },
      onWebSocketClose: (event: CloseEvent): void => {
        console.warn(
          '[WebSocket] socket closed',
          event.code,
          event.reason
        );
        this.unsubscribeAll();
        this.connectionStatusSubject.next('disconnected');
        this.handleConnectionFailure();
      },
    });
  }

  private subscribeToChannels(): void {
    if (!this.client?.connected) {
      console.warn('[WebSocket] subscribeToChannels skipped — client not connected');
      return;
    }

    this.unsubscribeAll();

    console.log(
      '[WebSocket] subscribing to /all/messages, /user/specific, /whatsapp/message, /whatsapp/chat-projection, /whatsapp/chat-activity, /user/kommuno/incoming-call, /kommuno/incoming-call',
    );

    this.allSubscription = this.client.subscribe(
      '/all/messages',
      (message: IMessage) => {
        const body = this.parseBody(message);
        console.log('[WebSocket] /all/messages →', body);
        this.allNotificationsSubject.next(body);
      }
    );

    this.ownSubscription = this.client.subscribe(
      '/user/specific',
      (message: IMessage) => {
        const body = this.parseBody(message);
        console.log('[WebSocket] /user/specific →', body);
        this.ownNotificationsSubject.next(body);
      }
    );

    this.whatsappSubscription = this.client.subscribe(
      '/whatsapp/message',
      (message: IMessage) => {
        const body = this.parseBody(message) as IFullWhatsappMessage;
        console.log('[WebSocket] /whatsapp/message →', body);
        this.whatsappMessagesSubject.next(body);
      }
    );

    this.whatsappChatProjectionSubscription = this.client.subscribe(
      '/whatsapp/chat-projection',
      (message: IMessage) => {
        const invalidation = this.parseChatProjectionInvalidation(message);
        if (invalidation) {
          this.whatsappChatProjectionSubject.next(invalidation);
        }
      },
    );

    this.whatsappChatActivitySubscription = this.client.subscribe(
      '/whatsapp/chat-activity',
      (message: IMessage) => {
        const invalidation = this.parseChatActivityInvalidation(message);
        if (invalidation) {
          this.whatsappChatActivitySubject.next(invalidation);
        }
      },
    );

    this.incomingCallUserSubscription = this.client.subscribe(
      '/user/kommuno/incoming-call',
      (message: IMessage) => {
        const alert = this.parseIncomingCallAlert(message);
        if (alert) {
          this.incomingCallAlertsSubject.next(alert);
        }
      },
    );

    this.incomingCallBroadcastSubscription = this.client.subscribe(
      '/kommuno/incoming-call',
      (message: IMessage) => {
        const alert = this.parseIncomingCallAlert(message);
        if (alert) {
          this.incomingCallAlertsSubject.next(alert);
        }
      },
    );
  }

  private unsubscribeAll(): void {
    if (
      this.allSubscription ||
      this.ownSubscription ||
      this.whatsappSubscription ||
      this.whatsappChatProjectionSubscription ||
      this.whatsappChatActivitySubscription ||
      this.incomingCallUserSubscription ||
      this.incomingCallBroadcastSubscription
    ) {
      console.log('[WebSocket] unsubscribing all channels');
    }
    this.allSubscription?.unsubscribe();
    this.allSubscription = undefined;
    this.ownSubscription?.unsubscribe();
    this.ownSubscription = undefined;
    this.whatsappSubscription?.unsubscribe();
    this.whatsappSubscription = undefined;
    this.whatsappChatProjectionSubscription?.unsubscribe();
    this.whatsappChatProjectionSubscription = undefined;
    this.whatsappChatActivitySubscription?.unsubscribe();
    this.whatsappChatActivitySubscription = undefined;
    this.incomingCallUserSubscription?.unsubscribe();
    this.incomingCallUserSubscription = undefined;
    this.incomingCallBroadcastSubscription?.unsubscribe();
    this.incomingCallBroadcastSubscription = undefined;
  }

  private websocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const isLocalDev =
      !environment.production && window.location.hostname === 'localhost';

    if (isLocalDev) {
      return `${protocol}${window.location.host}/api/ws`;
    }

    return `${protocol}${environment.endPoint}/api/ws`;
  }

  private parseBody(message: IMessage): any {
    try {
      return JSON.parse(message.body);
    } catch {
      return message.body;
    }
  }

  private parseChatProjectionInvalidation(
    message: IMessage,
  ): IWhatsappChatProjectionInvalidation | null {
    const rawPayload = this.parseBody(message);
    if (
      !rawPayload ||
      typeof rawPayload !== 'object' ||
      Array.isArray(rawPayload)
    ) {
      return null;
    }
    const keys = Object.keys(rawPayload);
    if (
      keys.length !== 2 ||
      !keys.every(
        (key) =>
          key === 'whatsappContactId' || key === 'projectionVersion',
      )
    ) {
      return null;
    }
    const payload =
      rawPayload as Partial<IWhatsappChatProjectionInvalidation>;
    const whatsappContactId = payload?.whatsappContactId;
    const projectionVersion = payload?.projectionVersion;
    if (
      typeof whatsappContactId !== 'number' ||
      !Number.isFinite(whatsappContactId) ||
      !Number.isInteger(whatsappContactId) ||
      whatsappContactId <= 0 ||
      typeof projectionVersion !== 'number' ||
      !Number.isFinite(projectionVersion) ||
      !Number.isInteger(projectionVersion) ||
      projectionVersion < 0
    ) {
      return null;
    }
    return { whatsappContactId, projectionVersion };
  }

  /**
   * W7.277j — strict parser for the content-free `/whatsapp/chat-activity`
   * payload. Accepts ONLY an object with exactly the two keys `activityId`
   * and `whatsappContactId`, both positive JavaScript-safe integers. Any
   * extra key — especially a content-bearing one — rejects the whole event;
   * the rejected body is never logged.
   */
  private parseChatActivityInvalidation(
    message: IMessage,
  ): IWhatsappChatActivityInvalidation | null {
    const rawPayload = this.parseBody(message);
    if (
      !rawPayload ||
      typeof rawPayload !== 'object' ||
      Array.isArray(rawPayload)
    ) {
      return null;
    }
    const keys = Object.keys(rawPayload);
    if (
      keys.length !== 2 ||
      !keys.every(
        (key) => key === 'activityId' || key === 'whatsappContactId',
      )
    ) {
      return null;
    }
    const payload = rawPayload as Partial<IWhatsappChatActivityInvalidation>;
    const activityId = payload?.activityId;
    const whatsappContactId = payload?.whatsappContactId;
    if (
      typeof activityId !== 'number' ||
      !Number.isSafeInteger(activityId) ||
      activityId <= 0 ||
      typeof whatsappContactId !== 'number' ||
      !Number.isSafeInteger(whatsappContactId) ||
      whatsappContactId <= 0
    ) {
      return null;
    }
    return { activityId, whatsappContactId };
  }

  /**
   * Validates the typed incoming-call contract without ever logging the
   * rejected body, which contains the caller's phone number.
   */
  private parseIncomingCallAlert(
    message: IMessage,
  ): IIncomingCallAlert | null {
    const rawPayload = this.parseBody(message);
    if (
      !rawPayload ||
      typeof rawPayload !== 'object' ||
      Array.isArray(rawPayload)
    ) {
      return null;
    }

    const payload = rawPayload as Partial<IIncomingCallAlert>;
    const sessionId =
      typeof payload.sessionId === 'string' ? payload.sessionId.trim() : '';
    const customerPhone =
      typeof payload.customerPhone === 'string'
        ? payload.customerPhone.trim()
        : '';

    if (
      !sessionId ||
      !customerPhone ||
      (payload.customerName != null &&
        typeof payload.customerName !== 'string') ||
      (payload.enquiryId != null && typeof payload.enquiryId !== 'string') ||
      (payload.leadId != null && typeof payload.leadId !== 'string') ||
      typeof payload.ringingAt !== 'string' ||
      typeof payload.targeted !== 'boolean'
    ) {
      return null;
    }

    return {
      sessionId,
      customerPhone,
      customerName: payload.customerName?.trim() || null,
      enquiryId: payload.enquiryId?.trim() || null,
      leadId: payload.leadId?.trim() || null,
      ringingAt: payload.ringingAt,
      targeted: payload.targeted,
    };
  }

  /**
   * Called from `onWebSocketClose` after every socket close. Increments
   * the consecutive-failure counter and, once it crosses
   * `MAX_RECONNECT_ATTEMPTS`, sets `reconnectDelay = 0` and deactivates
   * the client so StompJS stops re-scheduling failed reconnects. The
   * "infinite loop on localhost" was just StompJS faithfully retrying
   * every 5s forever — this caps it.
   */
  private handleConnectionFailure(): void {
    if (this.gaveUp || !this.client) {
      return;
    }

    this.consecutiveFailures += 1;

    if (this.consecutiveFailures < MAX_RECONNECT_ATTEMPTS) {
      console.warn(
        '[WebSocket] reconnect attempt',
        `${this.consecutiveFailures}/${MAX_RECONNECT_ATTEMPTS}`,
      );
      return;
    }

    console.warn(
      `[WebSocket] giving up after ${MAX_RECONNECT_ATTEMPTS} consecutive failed connects — ` +
        'call connect() again (e.g. refresh) to retry.',
    );
    this.gaveUp = true;
    this.client.reconnectDelay = 0;
    void this.client.deactivate();
    // Emit AFTER the internal state is set + reconnect scheduling is
    // stopped, so a synchronous subscriber that reacts immediately
    // (e.g. opens a refresh-prompt dialog) sees the client in its
    // final give-up state.
    this.gaveUpSubject.next(true);
  }
}
